import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, DollarSign, Calendar, Percent, FileText, User, Briefcase, Home, Car, GraduationCap, Check, Loader2, Building2 } from 'lucide-react'
import { LoansAPI, AccountsAPI, extractArray } from '../services/api'

// Mapeo de iconos por tipo de préstamo
const loanTypeIcons = {
  personal: <User className="w-5 h-5" />,
  mortgage: <Home className="w-5 h-5" />,
  auto: <Car className="w-5 h-5" />,
  business: <Briefcase className="w-5 h-5" />,
  student: <GraduationCap className="w-5 h-5" />,
  home_equity: <Building2 className="w-5 h-5" />,
  credit_builder: <DollarSign className="w-5 h-5" />
}

// Colores por tipo de préstamo
const loanTypeColors = {
  personal: 'bg-blue-50 text-blue-600',
  mortgage: 'bg-emerald-50 text-emerald-600',
  auto: 'bg-orange-50 text-orange-600',
  business: 'bg-purple-50 text-purple-600',
  student: 'bg-indigo-50 text-indigo-600',
  home_equity: 'bg-teal-50 text-teal-600',
  credit_builder: 'bg-amber-50 text-amber-600'
}

export default function LoanModal({ onClose, user, onApplyLoan }) {
  const [step, setStep] = useState(1)
  const [loanData, setLoanData] = useState({
    productId: '',
    amount: '',
    term: '',
    purpose: '',
    income: '',
    employment: '',
    collateral: ''
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const [applicationComplete, setApplicationComplete] = useState(false)
  const [applicationResult, setApplicationResult] = useState(null)
  const [error, setError] = useState(null)

  // Estados para datos de la API
  const [loanProducts, setLoanProducts] = useState([])
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [loanEstimate, setLoanEstimate] = useState(null)

  // Cargar productos de préstamos y cuentas al montar
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [productsRes, accountsRes] = await Promise.all([
          LoansAPI.getProducts(),
          AccountsAPI.getAll()
        ])

        console.log('Loan products API response:', JSON.stringify(productsRes, null, 2))

        if (productsRes.success) {
          console.log('productsRes.data:', productsRes.data)
          console.log('Is array?:', Array.isArray(productsRes.data))
          const products = Array.isArray(productsRes.data) ? productsRes.data : extractArray(productsRes) || []
          console.log('Extracted products:', products)
          // Mapear productos con iconos y colores
          const mappedProducts = products.map(p => ({
            ...p,
            icon: loanTypeIcons[p.loan_type] || <DollarSign className="w-5 h-5" />,
            color: loanTypeColors[p.loan_type] || 'bg-slate-50 text-slate-600',
            rate: `${p.base_apr}%`,
            maxRate: `${p.max_apr}%`,
            maxAmount: parseFloat(p.max_amount),
            minAmount: parseFloat(p.min_amount),
            // Generar términos disponibles
            terms: generateTerms(p.min_term_months, p.max_term_months)
          }))
          setLoanProducts(mappedProducts)
        }

        if (accountsRes.success) {
          setAccounts(extractArray(accountsRes, 'accounts'))
        }
      } catch (err) {
        console.error('Error fetching loan data:', err)
        setError(err.message || 'Error al cargar los productos de préstamo')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Generar array de términos disponibles
  const generateTerms = (min, max) => {
    const terms = []
    for (let t = min; t <= max; t += 12) {
      terms.push(t)
    }
    // Asegurar que el máximo esté incluido
    if (!terms.includes(max)) {
      terms.push(max)
    }
    return terms.sort((a, b) => a - b)
  }

  const selectedLoanProduct = loanProducts.find(p => p.id === loanData.productId || p.id === parseInt(loanData.productId))

  const handleInputChange = (e) => {
    setLoanData({
      ...loanData,
      [e.target.name]: e.target.value
    })
  }

  const handleNext = () => {
    if (step < 3) setStep(step + 1)
  }

  const handleBack = () => {
    if (step > 1) setStep(step - 1)
  }

  const calculateMonthlyPayment = () => {
    if (!loanData.amount || !loanData.term || !selectedLoanProduct) return 0

    const principal = parseFloat(loanData.amount)
    const rate = parseFloat(selectedLoanProduct.base_apr) / 100 / 12
    const term = parseInt(loanData.term)

    if (rate === 0) return (principal / term).toFixed(2)

    const monthlyPayment = (principal * rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1)
    return monthlyPayment.toFixed(2)
  }

  // Calcular estimación usando la API
  const fetchLoanEstimate = async () => {
    if (!loanData.productId || !loanData.amount || !loanData.term) return

    try {
      const res = await LoansAPI.calculate(
        loanData.productId,
        parseFloat(loanData.amount),
        parseInt(loanData.term)
      )
      if (res.success) {
        setLoanEstimate(res.data)
      }
    } catch (err) {
      console.error('Error calculating loan:', err)
    }
  }

  // Recalcular cuando cambien los datos del préstamo
  useEffect(() => {
    if (loanData.productId && loanData.amount && loanData.term) {
      fetchLoanEstimate()
    }
  }, [loanData.productId, loanData.amount, loanData.term])

  const handleSubmitApplication = async () => {
    setIsProcessing(true)
    setError(null)

    try {
      // Mapear employment status al formato esperado por el backend
      const employmentMap = {
        'full-time': 'employed',
        'part-time': 'employed',
        'self-employed': 'self_employed',
        'retired': 'retired',
        'student': 'student'
      }

      const applicationData = {
        productId: parseInt(loanData.productId),
        requestedAmount: parseFloat(loanData.amount),
        termMonths: parseInt(loanData.term),
        purpose: loanData.purpose,
        employmentStatus: employmentMap[loanData.employment] || loanData.employment,
        employerName: loanData.employerName || '',
        annualIncome: parseFloat(loanData.income) || 0,
        monthlyExpenses: parseFloat(loanData.monthlyExpenses) || 0
      }

      const res = await LoansAPI.apply(applicationData)

      if (res.success) {
        setApplicationComplete(true)
        setApplicationResult(res.data)

        if (onApplyLoan) {
          onApplyLoan(res.data)
        }

        setTimeout(() => {
          onClose()
        }, 3000)
      } else {
        setError(res.message || 'Error al enviar la solicitud')
      }
    } catch (err) {
      console.error('Error submitting application:', err)
      setError(err.message || 'Error al enviar la solicitud')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Loan Application</h2>
                <p className="text-sm text-slate-500">Step {step} of 3</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="mt-4 w-full bg-slate-100 rounded-full h-2">
              <div
                className="bg-slate-900 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>

          <div className="p-6">
            {/* Estado de carga */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-slate-600 animate-spin" />
                <span className="ml-3 text-slate-600">Loading loan products...</span>
              </div>
            ) : error && !applicationComplete ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Error</h3>
                <p className="text-sm text-red-600 mb-4">{error}</p>
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            ) : applicationComplete ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Application Submitted!</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Your {selectedLoanProduct?.name.toLowerCase()} application for ${parseFloat(loanData.amount).toLocaleString()} has been submitted.
                </p>
                <div className="bg-slate-50 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-xs text-slate-600 mb-2">Application Number:</p>
                  <p className="text-sm text-slate-900 font-mono">{applicationResult?.application_number || 'Processing...'}</p>
                </div>
                {applicationResult?.status === 'approved' && (
                  <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-sm font-medium text-emerald-800">Pre-approved!</p>
                    <p className="text-xs text-emerald-600 mt-1">
                      Your loan has been pre-approved. Monthly payment: ${applicationResult?.monthly_payment?.toFixed(2)}
                    </p>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-4">
                  We'll review your application and get back to you within 2-3 business days.
                </p>
              </motion.div>
            ) : isProcessing ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Processing Application...</h3>
                <p className="text-sm text-slate-600">Please wait while we submit your loan application</p>
              </motion.div>
            ) : (
              <div className="space-y-6">
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">Choose Your Loan Type</h3>

                    {loanProducts.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        No loan products available at this time.
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {loanProducts.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => setLoanData({...loanData, productId: product.id, loanType: product.loan_type})}
                            className={`p-5 rounded-xl transition-all text-left border-2 ${
                              loanData.productId === product.id
                                ? 'border-slate-900 bg-slate-50'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${product.color}`}>
                              {product.icon}
                            </div>

                            <h4 className="text-sm font-semibold text-slate-900 mb-3">{product.name}</h4>

                            <div className="space-y-2 mb-3">
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-600">Rate from:</span>
                                <span className="font-semibold text-slate-900">{product.rate} APR</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-600">Amount:</span>
                                <span className="font-semibold text-slate-900">${product.minAmount.toLocaleString()} - ${product.maxAmount.toLocaleString()}</span>
                              </div>
                            </div>

                            <p className="text-xs text-slate-600 line-clamp-2">{product.description}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">Loan Details</h3>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-slate-700 text-sm font-medium mb-2">Loan Amount</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                          <input
                            type="number"
                            name="amount"
                            placeholder="0"
                            value={loanData.amount}
                            onChange={handleInputChange}
                            min={selectedLoanProduct?.minAmount}
                            max={selectedLoanProduct?.maxAmount}
                            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-900 transition-colors"
                          />
                        </div>
                        {selectedLoanProduct && (
                          <p className="text-xs text-slate-600 mt-1">
                            Range: ${selectedLoanProduct.minAmount.toLocaleString()} - ${selectedLoanProduct.maxAmount.toLocaleString()}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-slate-700 text-sm font-medium mb-2">Loan Term (months)</label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                          <select
                            name="term"
                            value={loanData.term}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
                          >
                            <option value="">Select term</option>
                            {selectedLoanProduct?.terms.map((term) => (
                              <option key={term} value={term}>
                                {term} months ({Math.floor(term / 12)} {Math.floor(term / 12) === 1 ? 'year' : 'years'}{term % 12 > 0 ? ` ${term % 12}m` : ''})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-slate-700 text-sm font-medium mb-2">Loan Purpose</label>
                        <div className="relative">
                          <FileText className="absolute left-3 top-3 text-slate-400 w-5 h-5" />
                          <textarea
                            name="purpose"
                            placeholder="What will you use this loan for?"
                            value={loanData.purpose}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-900 transition-colors resize-none"
                          />
                        </div>
                      </div>
                    </div>

                    {loanData.amount && loanData.term && selectedLoanProduct && (
                      <div className="mt-6 bg-slate-50 rounded-lg p-5">
                        <h4 className="text-sm font-semibold text-slate-900 mb-4">Loan Summary</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-slate-600 mb-1">Amount</p>
                            <p className="text-sm font-bold text-slate-900">${parseFloat(loanData.amount).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-600 mb-1">Rate (from)</p>
                            <p className="text-sm font-bold text-slate-900">{selectedLoanProduct.rate} APR</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-600 mb-1">Term</p>
                            <p className="text-sm font-bold text-slate-900">{loanData.term} months</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-600 mb-1">Est. Monthly Payment</p>
                            <p className="text-sm font-bold text-slate-900">${loanEstimate?.monthlyPayment?.toFixed(2) || calculateMonthlyPayment()}</p>
                          </div>
                        </div>
                        {loanEstimate && (
                          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-slate-600 mb-1">Total Interest</p>
                              <p className="text-sm font-semibold text-slate-900">${loanEstimate.totalInterest?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-600 mb-1">Total Payment</p>
                              <p className="text-sm font-semibold text-slate-900">${loanEstimate.totalPayment?.toFixed(2)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">Financial Information</h3>

                    <div className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-slate-700 text-sm font-medium mb-2">Annual Income</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                            <input
                              type="number"
                              name="income"
                              placeholder="75000"
                              value={loanData.income}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-900 transition-colors"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-slate-700 text-sm font-medium mb-2">Employment Status</label>
                          <div className="relative">
                            <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                            <select
                              name="employment"
                              value={loanData.employment}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
                            >
                              <option value="">Select status</option>
                              <option value="full-time">Full-time Employee</option>
                              <option value="part-time">Part-time Employee</option>
                              <option value="self-employed">Self-employed</option>
                              <option value="retired">Retired</option>
                              <option value="student">Student</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {selectedLoanProduct?.requires_collateral && (
                        <div>
                          <label className="block text-slate-700 text-sm font-medium mb-2">
                            {selectedLoanProduct.loan_type === 'mortgage' ? 'Property Value' :
                             selectedLoanProduct.loan_type === 'auto' ? 'Vehicle Value' : 'Collateral Value'}
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                            <input
                              type="number"
                              name="collateral"
                              placeholder="Enter collateral value"
                              value={loanData.collateral}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-slate-900 transition-colors"
                            />
                          </div>
                        </div>
                      )}

                      {/* Final Review */}
                      <div className="bg-slate-50 rounded-lg p-5">
                        <h4 className="text-sm font-semibold text-slate-900 mb-4">Application Summary</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Loan Product:</span>
                            <span className="text-slate-900 font-medium">{selectedLoanProduct?.name}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Amount:</span>
                            <span className="text-slate-900 font-semibold">${parseFloat(loanData.amount).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Term:</span>
                            <span className="text-slate-900 font-medium">{loanData.term} months</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Interest Rate (from):</span>
                            <span className="text-slate-900 font-medium">{selectedLoanProduct?.rate} APR</span>
                          </div>
                          <div className="border-t border-slate-200 pt-2 mt-2">
                            <div className="flex justify-between">
                              <span className="text-slate-600 text-sm">Est. Monthly Payment:</span>
                              <span className="text-slate-900 font-bold text-lg">${loanEstimate?.monthlyPayment?.toFixed(2) || calculateMonthlyPayment()}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Error message */}
                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-sm text-red-600">{error}</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Navigation */}
                <div className="flex justify-between pt-6">
                  {step > 1 && (
                    <button
                      onClick={handleBack}
                      className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                    >
                      Back
                    </button>
                  )}

                  <div className="ml-auto">
                    {step < 3 ? (
                      <button
                        onClick={handleNext}
                        disabled={
                          (step === 1 && !loanData.productId) ||
                          (step === 2 && (!loanData.amount || !loanData.term || !loanData.purpose))
                        }
                        className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmitApplication}
                        disabled={!loanData.income || !loanData.employment || isProcessing}
                        className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                      >
                        {isProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                        Submit Application
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
